import { Component, Input, Output, EventEmitter, OnInit, OnChanges, ViewChild, ElementRef } from '@angular/core';
import { FormControl } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { GestiuneAjutoareService } from '../../../gestiune-ajutoare/gestiune-ajutoare.service';
import { UsersService } from 'src/app/services/users.service';
import { AppFilesService } from 'src/app/services/app-files.service';
import { environment } from 'src/environments/environment';
import { helper } from 'src/environments/helper';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-pre-validare-cerere',
    templateUrl: './pre-validare-cerere.component.html',
    styleUrls: ['./pre-validare-cerere.component.scss'],
})
export class PreValidareCerereComponent implements OnInit, OnChanges {
    @Input() cerere: any;
    @Input() preValidare: any;
    @Output() refreshCerere = new EventEmitter<void>();

    helper = helper;
    isLoading = false;
    uploadAccept = '.jpg,.jpeg,.png,.pdf,.doc,.docx,.odt,.ods,.csv,.xlsx,.xls';

    // Users (toți operatorii)
    allUsers: any[] = [];
    responsabilControl = new FormControl<any>('');
    filteredResponsabili$!: Observable<any[]>;
    anafControl = new FormControl<any>('');
    filteredAnaf$!: Observable<any[]>;

    // Fișă
    fisaConcluzii = '';
    fisaPropuneri = '';
    fisaFamilieConfirmat: boolean | null = null;
    fisaFamilieObs = '';

    // Replace file input refs
    private pendingReplaceDocId: number | null = null;
    @ViewChild('replaceFileInput') replaceFileInput!: ElementRef<HTMLInputElement>;

    private uploadUrl = environment.interop.basePath + 'api/cerere/upload-file';
    private pvBaseUrl = environment.interop.basePath + 'api/cerere/ajutoare/pre-validare/';

    constructor(
        private http: HttpClient,
        private service: GestiuneAjutoareService,
        private usersService: UsersService,
        private appFileService: AppFilesService,
    ) {}

    ngOnInit(): void {
        this.loadUsers();
        this.syncFisaFields();
    }

    ngOnChanges(): void {
        this.syncFisaFields();
    }

    get pv(): any { return this.preValidare; }
    get cerereId(): number { return this.cerere?.id_cerere; }

    get docsAnaf(): any[] { return this.pv?.documents?.filter((d: any) => d.tip === 'anaf') || []; }
    get anexeActuale(): any[] { return this.pv?.documents?.filter((d: any) => d.tip === 'anexa_responsabil' && d.is_current) || []; }
    get anexeIstoric(): any[] { return this.pv?.documents?.filter((d: any) => d.tip === 'anexa_responsabil' && !d.is_current) || []; }
    get docsFisa(): any[] { return this.pv?.documents?.filter((d: any) => d.tip === 'fisa_verificare') || []; }

    // ── Load all users (same as "adaugă coleg") ──

    loadUsers(): void {
        this.usersService.getUsersActive().subscribe({
            next: (res: any) => {
                this.allUsers = (res?.data || res || []).map((u: any) => ({ id: u.id_user || u.id, name: u.name }));
                this.setupAutocomplete();
            },
            error: () => { this.allUsers = []; this.setupAutocomplete(); },
        });
    }

    setupAutocomplete(): void {
        this.filteredResponsabili$ = this.responsabilControl.valueChanges.pipe(
            startWith(''), map(v => typeof v === 'string' ? v : ''),
            map(s => s ? this.allUsers.filter(u => u.name.toLowerCase().includes(s.toLowerCase())) : this.allUsers)
        );
        this.filteredAnaf$ = this.anafControl.valueChanges.pipe(
            startWith(''), map(v => typeof v === 'string' ? v : ''),
            map(s => s ? this.allUsers.filter(u => u.name.toLowerCase().includes(s.toLowerCase())) : this.allUsers)
        );
    }

    displayUser(user: any): string { return user?.name || ''; }

    // ── Download doc ──

    downloadDoc(docId: number): void {
        this.appFileService.downloadFile(this.pvBaseUrl + this.cerereId + '/doc/' + docId + '/download').catch(err => console.error(err));
    }

    // ── Repartizare ──

    repartizeaza(): void {
        const user = this.responsabilControl.value;
        if (!user?.id) return;
        this.service.repartizeazaResponsabil(this.cerereId, {
            user_id: user.id, user_name: user.name,
            directie_id: this.cerere?.id_directie, directie_name: this.cerere?.directie || '',
            compartiment_id: this.cerere?.type_compartiment_id || 352, compartiment_name: this.cerere?.compartiment || '',
        }).subscribe({
            next: () => { Swal.fire({ icon: 'success', title: 'Repartizat', text: `Cererea a fost repartizată către ${user.name}.`, timer: 2000 }); this.refreshCerere.emit(); },
            error: (err) => Swal.fire({ icon: 'error', title: 'Eroare', text: err.error?.message || 'Eroare la repartizare.' }),
        });
    }

    // ── Pas 1 ──

    aprobaPas1(): void {
        Swal.fire({
            title: 'Aprobare documentație', text: 'Confirmați aprobarea documentației?',
            showCancelButton: true, confirmButtonText: 'Aprobă', cancelButtonText: 'Anulează', reverseButtons: true, showLoaderOnConfirm: true,
            preConfirm: () => this.service.aprobaPas1(this.cerereId).toPromise(),
            allowOutsideClick: () => !Swal.isLoading(),
        }).then(r => { if (r.isConfirmed) { Swal.fire({ icon: 'success', title: 'Aprobat', timer: 1500 }); this.refreshCerere.emit(); } });
    }

    respingePas1(): void {
        Swal.fire({
            title: 'Respingere documentație',
            input: 'textarea', inputLabel: 'Motivul respingerii', inputPlaceholder: 'Descrieți motivul...',
            inputValidator: (v) => !v ? 'Motivul este obligatoriu' : null,
            showCancelButton: true, confirmButtonText: 'Respinge', cancelButtonText: 'Anulează',
            confirmButtonColor: '#d33', reverseButtons: true, showLoaderOnConfirm: true,
            preConfirm: (motiv) => this.service.respingePas1(this.cerereId, { motiv }).toPromise(),
            allowOutsideClick: () => !Swal.isLoading(),
        }).then(r => { if (r.isConfirmed) { Swal.fire({ icon: 'success', title: 'Respins', timer: 1500 }); this.refreshCerere.emit(); } });
    }

    // ── Pas 2 ──

    alocaAnaf(): void {
        const user = this.anafControl.value;
        if (!user?.id) return;
        this.service.alocaAnaf(this.cerereId, { anaf_user_id: user.id, anaf_user_name: user.name }).subscribe({
            next: () => { Swal.fire({ icon: 'success', title: 'Alocat', text: `Alocat către ${user.name}.`, timer: 2000 }); this.refreshCerere.emit(); },
            error: (err) => Swal.fire({ icon: 'error', title: 'Eroare', text: err.error?.message || 'Eroare.' }),
        });
    }

    uploadDocsAnaf(event: Event): void {
        const files = (event.target as HTMLInputElement).files;
        if (!files || files.length === 0) return;
        this.uploadMultiple(Array.from(files), (file, data) => {
            this.service.uploadDocAnaf(this.cerereId, { filename: data.filename, old_name: file.name, ext: data.ext }).toPromise();
        });
    }

    confirmDeleteDocAnaf(docId: number): void {
        Swal.fire({
            title: 'Confirmare ștergere', text: 'Sigur doriți să ștergeți acest document?', icon: 'warning',
            showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Șterge', cancelButtonText: 'Anulează',
        }).then(r => {
            if (r.isConfirmed) {
                this.service.deleteDocAnaf(this.cerereId, docId).subscribe({ next: () => this.refreshCerere.emit() });
            }
        });
    }

    completeazaPas2(): void {
        Swal.fire({
            title: 'Completare verificare ANAF', text: 'Confirmați completarea verificării?',
            showCancelButton: true, confirmButtonText: 'Completează', cancelButtonText: 'Anulează', reverseButtons: true, showLoaderOnConfirm: true,
            preConfirm: () => this.service.completeazaPas2(this.cerereId).toPromise(),
            allowOutsideClick: () => !Swal.isLoading(),
        }).then(r => { if (r.isConfirmed) { Swal.fire({ icon: 'success', title: 'Completat', timer: 1500 }); this.refreshCerere.emit(); } });
    }

    // ── Anexe ──

    uploadAnexe(event: Event): void {
        const files = (event.target as HTMLInputElement).files;
        if (!files || files.length === 0) return;
        this.uploadMultiple(Array.from(files), (file, data) => {
            this.service.uploadAnexa(this.cerereId, { filename: data.filename, old_name: file.name, ext: data.ext }).toPromise();
        });
    }

    confirmReplaceAnexa(docId: number): void {
        Swal.fire({
            title: 'Confirmare înlocuire', text: 'Versiunea curentă va fi mutată la istoric. Continuați?',
            showCancelButton: true, confirmButtonText: 'Alege fișier', cancelButtonText: 'Anulează', reverseButtons: true,
        }).then(r => {
            if (r.isConfirmed) {
                this.pendingReplaceDocId = docId;
                this.replaceFileInput.nativeElement.click();
            }
        });
    }

    onReplaceFileSelected(event: Event): void {
        const file = (event.target as HTMLInputElement).files?.[0];
        if (!file || !this.pendingReplaceDocId) return;
        const docId = this.pendingReplaceDocId;
        this.pendingReplaceDocId = null;
        this.doUpload(file, (data: any) => {
            this.service.replaceAnexa(this.cerereId, docId, { filename: data.filename, old_name: file.name, ext: data.ext }).subscribe({
                next: () => this.refreshCerere.emit(),
            });
        });
    }

    confirmDeleteAnexa(docId: number): void {
        Swal.fire({
            title: 'Confirmare ștergere', text: 'Sigur doriți să ștergeți această anexă?', icon: 'warning',
            showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Șterge', cancelButtonText: 'Anulează',
        }).then(r => {
            if (r.isConfirmed) {
                this.service.deleteAnexa(this.cerereId, docId).subscribe({ next: () => this.refreshCerere.emit() });
            }
        });
    }

    // ── Fișă verificare ──

    syncFisaFields(): void {
        if (this.pv) {
            this.fisaConcluzii = this.pv.fisa_concluzii || '';
            this.fisaPropuneri = this.pv.fisa_propuneri || '';
            this.fisaFamilieConfirmat = this.pv.fisa_familie_confirmat;
            this.fisaFamilieObs = this.pv.fisa_familie_obs || '';
        }
    }

    saveFisaFields(): void {
        this.service.saveFisaFields(this.cerereId, {
            concluzii: this.fisaConcluzii, propuneri: this.fisaPropuneri,
            familie_confirmat: this.fisaFamilieConfirmat, familie_obs: this.fisaFamilieObs,
        }).subscribe({
            next: () => { Swal.fire({ icon: 'success', title: 'Salvat', timer: 1500 }); this.refreshCerere.emit(); },
        });
    }

    generateFisaWord(): void {
        this.appFileService.downloadFile(this.service.getFisaWordUrl(this.cerereId)).catch(err => console.error(err));
    }

    uploadFiseSemnate(event: Event): void {
        const files = (event.target as HTMLInputElement).files;
        if (!files || files.length === 0) return;
        this.uploadMultiple(Array.from(files), (file, data) => {
            this.service.uploadFisaSemnata(this.cerereId, { filename: data.filename, old_name: file.name, ext: data.ext }).toPromise();
        });
    }

    // ── Upload helper ──

    private uploadMultiple(files: File[], handler: (file: File, data: any) => void): void {
        let completed = 0;
        for (const file of files) {
            this.doUpload(file, (data: any) => {
                handler(file, data);
                completed++;
                if (completed === files.length) {
                    this.refreshCerere.emit();
                }
            });
        }
    }

    private doUpload(file: File, callback: (data: any) => void): void {
        const formData = new FormData();
        formData.append('file', file);
        this.http.post<any>(this.uploadUrl, formData).subscribe({
            next: (res: any) => { if (res?.data) callback(res.data); },
            error: () => Swal.fire({ icon: 'error', title: 'Eroare la upload' }),
        });
    }
}
